import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  BadgeCheck,
  CalendarDays,
  CalendarPlus,
  CheckCircle2,
  Home,
  MapPin,
  CreditCard,
  FileCheck2,
  FileText,
  Loader2,
  Paperclip,
  Users,
} from 'lucide-react';
import ChoixFichier, { type PoigneeFichier } from '../components/ChoixFichier';
import { deposerMaPiece, fetchMonAdhesion, renouvelerMonAdhesion } from '../services/api';

function euros(centimes: number): string {
  return `${(centimes / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/**
 * L'espace d'une famille.
 *
 * CE QU'ON Y FAIT SANS DÉRANGER PERSONNE
 * ---------------------------------------------------------------------------
 * Régler sa cotisation, éditer son justificatif de paiement, déposer les
 * pièces que le club réclame, se réinscrire pour l'année suivante, et savoir
 * dans quelle équipe on est. Ce sont les questions pour lesquelles on appelle
 * le président un dimanche soir.
 *
 * CE QUI A CHANGÉ, ET POURQUOI
 * ---------------------------------------------------------------------------
 * La page ne montrait qu'une licence — la première rendue par la base, donc
 * celle qui avait été saisie en premier. Une famille pouvait lire la
 * cotisation de l'an dernier en croyant lire celle de cette année, et dans une
 * association omnisports elle ne voyait qu'une de ses deux sections : la
 * seconde restait impayée sans que rien ne l'indique.
 *
 * Elle les montre donc toutes, la saison en cours en tête.
 *
 * Un compte peut couvrir deux enfants : la page rend une liste, pas une fiche.
 */
export default function MonAdhesionPage() {
  const query = useQuery({ queryKey: ['mon-adhesion'], queryFn: fetchMonAdhesion });

  if (query.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  const adherents: any[] = query.data?.adherents ?? [];

  if (adherents.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <Users className="w-10 h-10 mx-auto text-gray-300 mb-4" />
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Aucune adhésion à votre nom</h1>
        <p className="text-sm text-gray-500">
          Si vous pensez qu'il y a une erreur, écrivez au bureau : c'est lui qui tient le registre.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Mon adhésion</h1>
        <p className="text-sm text-gray-500 mt-1">
          {adherents.length > 1
            ? 'Les adhésions rattachées à votre compte.'
            : 'Votre cotisation, vos pièces et votre équipe.'}
        </p>
      </div>

      {adherents.map((a) => (
        <FicheFamille key={a.id} adherent={a} documentsAttendus={query.data?.documentsAttendus ?? []} />
      ))}
    </div>
  );
}

function FicheFamille({ adherent, documentsAttendus }: { adherent: any; documentsAttendus: any[] }) {
  const queryClient = useQueryClient();
  const fichierRef = useRef<PoigneeFichier>(null);
  const [type, setType] = useState('');
  const [libelle, setLibelle] = useState('');
  const [refus, setRefus] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);

  /*
    LA SAISON EN COURS, DITE PAR LE SERVEUR PLUTÔT QUE DEVINÉE DU RANG.

    « licences[0] » ne garantissait rien : l'ordre venait de la base, donc de
    l'ordre de saisie. Le serveur marque désormais chaque licence, et les rend
    déjà triées — saison en cours d'abord, puis les années passées.
  */
  const licences: any[] = adherent.licences ?? [];
  const cetteAnnee = licences.filter((l) => l.courante);
  const passees = licences.filter((l) => !l.courante);

  const deposer = useMutation({
    mutationFn: (formulaire: FormData) => deposerMaPiece(adherent.id, formulaire),
    onSuccess: (d: any) => {
      setSucces(d.message);
      setRefus(null);
      setType('');
      setLibelle('');
      fichierRef.current?.vider();
      queryClient.invalidateQueries({ queryKey: ['mon-adhesion'] });
      setTimeout(() => setSucces(null), 4000);
    },
    onError: (e: Error) => setRefus(e.message),
  });

  const envoyer = () => {
    const fichier = fichierRef.current?.fichier() ?? null;
    if (!fichier) {
      setRefus("Choisissez d'abord un fichier.");
      return;
    }
    if (type === '') {
      setRefus("Dites de quel type de pièce il s'agit.");
      return;
    }
    if (type === 'autre' && libelle.trim() === '') {
      setRefus("Précisez de quel document il s'agit.");
      return;
    }

    setRefus(null);
    const formulaire = new FormData();
    formulaire.append('document', fichier);
    formulaire.append('type', type);
    if (libelle.trim() !== '') formulaire.append('libelle', libelle.trim());
    deposer.mutate(formulaire);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100">
        <h2 className="font-semibold text-gray-900">{adherent.nomComplet}</h2>
        <p className="text-xs text-gray-500">
          {[adherent.age !== null ? `${adherent.age} ans` : null, adherent.discipline]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </div>

      {/* --------------------------- Ses cotisations --------------------------- */}
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          {cetteAnnee.length > 1 ? 'Mes cotisations' : 'Cotisation'}
        </h3>

        {cetteAnnee.length === 0 ? (
          <p className="text-sm text-gray-500">Aucune inscription enregistrée pour l'instant.</p>
        ) : (
          <div className="space-y-5">
            {cetteAnnee.map((l: any) => (
              <BlocCotisation key={l.id} licence={l} nommer={cetteAnnee.length > 1} />
            ))}
          </div>
        )}

        {/*
          LES ANNÉES PASSÉES, REPLIÉES ET NON EFFACÉES.

          Elles servent une fois par an, quand un organisme réclame un
          justificatif de la saison précédente. Les déployer d'office ferait
          descendre la cotisation en cours sous trois années d'historique — or
          c'est elle qu'on vient voir.
        */}
        {passees.length > 0 && (
          <details className="mt-5 border-t border-gray-100 pt-3">
            <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900">
              Mes saisons précédentes ({passees.length})
            </summary>
            <ul className="mt-3 space-y-2">
              {passees.map((l: any) => (
                <li
                  key={l.id}
                  className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm"
                >
                  <span className="text-gray-700">
                    {l.saison}
                    {l.discipline ? ` · ${l.discipline}` : ''}
                    {l.categorie ? ` · ${l.categorie}` : ''}
                  </span>
                  <span className="flex items-center gap-3">
                    <span className={l.reste > 0 ? 'text-amber-700 tabular-nums' : 'text-gray-500 tabular-nums'}>
                      {l.reste > 0 ? `${euros(l.reste)} dus` : `${euros(l.regle)} réglés`}
                    </span>
                    {l.attestationUrl && (
                      <a
                        href={l.attestationUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-primary-700 hover:text-primary-800"
                      >
                        <FileCheck2 className="w-3.5 h-3.5" />
                        Justificatif
                      </a>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>

      {/* -------------------------- La réinscription --------------------------- */}
      {adherent.renouvellement !== null && adherent.renouvellement !== undefined && (
        <Reinscription adherent={adherent} />
      )}

      {/* ------------------------------ Les pièces ------------------------------ */}
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-primary-600" />
          Mes pièces
        </h3>

        {adherent.documentsManquants?.length > 0 ? (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
            Le club attend encore : {adherent.documentsManquants.join(', ')}.
          </p>
        ) : (
          <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-3">
            Votre dossier est complet.
          </p>
        )}

        {adherent.documents?.length > 0 && (
          <ul className="divide-y divide-gray-100 mb-3">
            {adherent.documents.map((d: any) => (
              <li key={d.id} className="flex items-center gap-3 py-2 text-sm">
                <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                <a
                  href={d.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 min-w-0 truncate text-gray-800 hover:text-primary-700"
                >
                  {d.libelle}
                </a>
                {d.valableJusquau && (
                  <span
                    className={`text-xs rounded-full px-2 py-0.5 ${
                      d.perime ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {d.perime ? 'périmé' : `jusqu'au ${d.valableJusquau}`}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 border border-gray-100 p-3">
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
          >
            <option value="">Type de pièce…</option>
            {documentsAttendus.map((d: any) => (
              <option key={d.code} value={d.code}>
                {d.libelle}
              </option>
            ))}
            <option value="autre">Autre document</option>
          </select>

          {type === 'autre' && (
            <input
              value={libelle}
              onChange={(e) => setLibelle(e.target.value)}
              placeholder="De quel document s'agit-il ? *"
              className="px-3 py-1.5 rounded-lg border border-amber-300 bg-amber-50 text-sm"
            />
          )}

          <ChoixFichier ref={fichierRef} libelle="Choisir la pièce…" />

          <button
            onClick={envoyer}
            disabled={deposer.isPending}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60"
          >
            {deposer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Transmettre
          </button>

          {refus && (
            <p className="w-full text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
              {refus}
            </p>
          )}
          {succes && (
            <p className="w-full text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-1.5">
              {succes}
            </p>
          )}
        </div>
      </div>

      {/* ---------------------------- Ses rencontres ---------------------------- */}
      <Rencontres calendrier={adherent.rencontres} prenom={adherent.prenom ?? ''} />

      {/* ------------------------------ Les équipes ----------------------------- */}
      <div className="px-5 py-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <Users className="w-4 h-4 text-primary-600" />
          Mon équipe
        </h3>

        {adherent.equipes?.length > 0 ? (
          <ul className="space-y-2">
            {adherent.equipes.map((e: any, i: number) => (
              <li key={i} className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-sm">
                <span className="block font-medium text-gray-900">
                  {e.nom}
                  {e.numero && <span className="text-gray-500"> · n° {e.numero}</span>}
                  {e.role && <span className="text-gray-500"> · {e.role}</span>}
                </span>
                <span className="block text-xs text-gray-500">
                  {[e.saison, e.engagement, e.entraineur ? `encadré par ${e.entraineur}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500">
            Aucune équipe pour l'instant. L'entraîneur compose les groupes en début de saison.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Une cotisation, et ce qu'il en reste à faire.
 *
 * POURQUOI C'EST UN COMPOSANT ET PLUS UN BLOC DANS LA PAGE
 * ---------------------------------------------------------------------------
 * Dans une association omnisports, une même personne en a plusieurs — le judo
 * et la gymnastique, chacune avec son montant, son échéance et son lien de
 * paiement. Chacune doit pouvoir être réglée sans que l'autre bouge : les
 * mélanger ferait payer deux fois la même, ou croire les deux soldées quand
 * une seule l'est.
 */
function BlocCotisation({ licence, nommer }: { licence: any; nommer: boolean }) {
  return (
    <div className={nommer ? 'rounded-lg border border-gray-200 p-4' : ''}>
      {nommer && (
        <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-900">
          <BadgeCheck className="w-4 h-4 text-primary-600" />
          {licence.discipline}
        </p>
      )}

      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="text-sm text-gray-500">Saison {licence.saison}</span>
        {licence.categorie && <span className="text-sm text-gray-500">{licence.categorie}</span>}
        {licence.numero && <span className="text-sm text-gray-500">Licence n° {licence.numero}</span>}
      </div>

      <p className="mt-2 text-2xl font-semibold tabular-nums text-gray-900">
        {licence.reste > 0 ? euros(licence.reste) : euros(licence.montant)}
        <span className="ml-2 text-sm font-normal text-gray-500">
          {licence.reste > 0 ? 'restant à régler' : 'réglés — merci'}
        </span>
      </p>

      {licence.regle > 0 && licence.reste > 0 && (
        <p className="text-sm text-gray-500 mt-1">
          Déjà versé : {euros(licence.regle)} sur {euros(licence.montant)}
        </p>
      )}

      {licence.enRetard && (
        <p className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>L'échéance du {licence.echeance} est passée.</span>
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        {licence.lienPaiement && licence.reste > 0 && (
          <a
            href={licence.lienPaiement}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
          >
            <CreditCard className="w-4 h-4" />
            Régler {euros(licence.reste)}
          </a>
        )}

        {/*
          LE JUSTIFICATIF, ÉDITABLE PAR LA FAMILLE ELLE-MÊME.

          C'est le document qu'on produit à son comité d'entreprise ou à sa
          mairie pour se faire rembourser une part de la cotisation. Il
          existait déjà, mais seul le bureau pouvait l'éditer : la famille
          écrivait un mail, quelqu'un l'ouvrait trois jours plus tard, et le
          délai de la demande était passé.
        */}
        {licence.attestationUrl && (
          <a
            href={licence.attestationUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-primary-200 text-primary-700 text-sm font-medium hover:bg-primary-50"
            title="Le document à remettre à votre comité d'entreprise ou à votre mairie"
          >
            <FileCheck2 className="w-4 h-4" />
            Justificatif de paiement
          </a>
        )}
      </div>

      {licence.reglements?.length > 0 && (
        <ul className="mt-4 space-y-1 text-sm text-gray-600">
          {licence.reglements.map((r: any, i: number) => (
            <li key={i} className="flex items-center gap-2">
              {r.annule === true ? (
                <>
                  <AlertCircle className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span className="text-gray-400 line-through">
                    {euros(r.montant)} — {r.modeLibelle} le {r.recuLe}
                  </span>
                  <span className="text-xs text-gray-500">annulé</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                  <span>
                    {euros(r.montant)} — {r.modeLibelle} le {r.recuLe}
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Se réinscrire pour l'année suivante.
 *
 * POURQUOI CE BOUTON EXISTE ICI PLUTÔT QUE DANS UN COURRIER
 * ---------------------------------------------------------------------------
 * La réinscription est le geste que le club redoute de rater : une famille qui
 * ne la fait pas ne dit rien, elle ne revient simplement pas — et l'on s'en
 * aperçoit à la première séance de septembre, quand il manque huit joueurs.
 *
 * Le lui proposer ici, au moment où elle regarde sa cotisation soldée, coûte
 * moins qu'une campagne de relance et arrive deux mois plus tôt.
 *
 * CE QUE LE CLIC CRÉE, ET CE QU'IL NE CRÉE PAS
 * ---------------------------------------------------------------------------
 * Une demande enregistrée, au tarif du club pour la catégorie d'âge — qui a
 * changé, puisque l'enfant a un an de plus. Rien n'est encaissé : le règlement
 * suit, par le lien de paiement ou en main propre. Le texte le dit, pour que
 * personne ne croie avoir payé.
 */
function Reinscription({ adherent }: { adherent: any }) {
  const queryClient = useQueryClient();
  const renouvellement = adherent.renouvellement;
  const disciplines: any[] = renouvellement.disciplines ?? [];

  const [choisie, setChoisie] = useState<string>(
    disciplines.length > 0 ? String(disciplines[0].id) : '',
  );
  const [message, setMessage] = useState<string | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  const demander = useMutation({
    mutationFn: () =>
      renouvelerMonAdhesion(adherent.id, choisie === '' ? undefined : Number(choisie)),
    onSuccess: (d: any) => {
      setMessage(d.message);
      setRefus(null);
      queryClient.invalidateQueries({ queryKey: ['mon-adhesion'] });
    },
    onError: (e: Error) => setRefus(e.message),
  });

  /*
    Rien à proposer : il est déjà inscrit partout où il pouvait l'être.
    Un bouton qui ne mènerait qu'à un refus vaut moins que pas de bouton.
  */
  if (disciplines.length === 0) {
    return (
      <div className="px-5 py-4 border-b border-gray-100">
        <p className="flex items-start gap-2 text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {adherent.nomComplet} est déjà réinscrit pour la saison {renouvellement.saison}.
          </span>
        </p>
      </div>
    );
  }

  return (
    <div className="px-5 py-4 border-b border-gray-100">
      <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
        <CalendarPlus className="w-4 h-4 text-primary-600" />
        Saison {renouvellement.saison}
      </h3>
      <p className="text-sm text-gray-500 mb-3">
        Les inscriptions sont ouvertes. Votre demande est enregistrée tout de suite ; vous réglerez
        la cotisation ensuite, depuis cette page ou auprès du bureau.
      </p>

      {message !== null ? (
        <p className="flex items-start gap-2 text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{message}</span>
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {disciplines.length > 1 && (
            <select
              value={choisie}
              onChange={(e) => setChoisie(e.target.value)}
              className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
            >
              {disciplines.map((d: any) => (
                <option key={d.id} value={String(d.id)}>
                  {d.nom} — {d.federation}
                </option>
              ))}
            </select>
          )}

          <button
            onClick={() => demander.mutate()}
            disabled={demander.isPending}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60 min-h-[44px]"
          >
            {demander.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CalendarPlus className="w-4 h-4" />
            )}
            {disciplines.length > 1
              ? 'Me réinscrire dans cette discipline'
              : `Me réinscrire pour ${renouvellement.saison}`}
          </button>
        </div>
      )}

      {refus !== null && (
        <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          {refus}
        </p>
      )}
    </div>
  );
}

/**
 * « samedi 14 mars à 15:00 » — la date telle qu'on se la dit.
 *
 * Le jour de la semaine est écrit en toutes lettres, et il est le premier mot :
 * c'est lui qu'on cherche du regard sur un calendrier sportif, avant le
 * quantième. Une date rendue « 14/03/2026 15:00 » oblige à compter sur ses
 * doigts pour savoir si c'est ce week-end.
 */
function jourEtHeure(iso: string | null): string {
  if (iso === null) return '';
  const d = new Date(iso);
  return d.toLocaleString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const PLACE: Record<string, { texte: string; classe: string }> = {
  convoque: { texte: 'convoqué', classe: 'bg-primary-100 text-primary-800' },
  present: { texte: 'présent', classe: 'bg-green-100 text-green-800' },
  absent: { texte: 'absent', classe: 'bg-red-100 text-red-700' },
  excuse: { texte: 'excusé', classe: 'bg-gray-100 text-gray-600' },
};

const ISSUE: Record<string, { texte: string; classe: string }> = {
  gagnee: { texte: 'gagné', classe: 'text-green-700' },
  nulle: { texte: 'match nul', classe: 'text-gray-600' },
  perdue: { texte: 'perdu', classe: 'text-red-700' },
};

/**
 * Ce qu'il joue, et ce qu'il a joué.
 *
 * POURQUOI CETTE SECTION EXISTE
 * ---------------------------------------------------------------------------
 * C'est la question qu'un licencié pose chaque semaine : « est-ce que je joue
 * dimanche, et où ? ». Elle vivait dans un groupe de discussion où l'entraîneur
 * écrivait « rendez-vous 13 h 30 au stade de Villeneuve » le vendredi soir, et
 * où trois parents redemandaient l'adresse le dimanche matin parce que le
 * message était remonté.
 *
 * LE PROCHAIN MATCH EN PREMIER, EN GRAND
 * ---------------------------------------------------------------------------
 * C'est la seule ligne qu'on vient lire dans quatre-vingt-dix pour cent des
 * visites. Les autres suivent, et l'historique se replie : il sert une fois
 * par saison, quand on se demande combien de fois on a joué.
 */
function Rencontres({ calendrier, prenom }: { calendrier: any; prenom: string }) {
  const aVenir: any[] = calendrier?.aVenir ?? [];
  const passees: any[] = calendrier?.passees ?? [];
  const bilan = calendrier?.bilan ?? null;

  /*
    Rien à montrer : la section disparaît plutôt que d'afficher un cadre vide.
    Un club de gymnastique ou de loisir ne pose pas de calendrier, et lui
    laisser une boîte « aucune rencontre » lui répète chaque semaine qu'il
    n'utilise pas une fonction dont il n'a pas l'usage.
  */
  if (aVenir.length === 0 && passees.length === 0) {
    return null;
  }

  const [prochaine, ...suivantes] = aVenir;

  return (
    <div className="px-5 py-4 border-b border-gray-100">
      <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <CalendarDays className="w-4 h-4 text-primary-600" />
        Mes rencontres
      </h3>

      {prochaine !== undefined && (
        <div className="rounded-lg border border-primary-200 bg-primary-50/60 p-4">
          <p className="text-xs uppercase tracking-wide text-primary-800/70">Prochaine rencontre</p>

          <p className="mt-1 text-lg font-semibold text-gray-900">
            {prochaine.aDomicile ? 'Contre' : 'Chez'} {prochaine.adversaire}
          </p>

          <p className="mt-0.5 text-sm text-gray-700 first-letter:uppercase">
            {jourEtHeure(prochaine.debut)}
          </p>

          <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1.5">
              {prochaine.aDomicile ? (
                <>
                  <Home className="w-3.5 h-3.5" />À domicile
                </>
              ) : (
                <>
                  <MapPin className="w-3.5 h-3.5" />
                  Déplacement
                </>
              )}
            </span>
            {prochaine.lieu && <span>{prochaine.lieu}</span>}
            {prochaine.competition && <span>{prochaine.competition}</span>}
            {prochaine.equipe && <span>{prochaine.equipe}</span>}
            {prochaine.discipline && <span>{prochaine.discipline}</span>}
          </p>

          {prochaine.maPlace && (
            <p className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                  PLACE[prochaine.maPlace]?.classe ?? 'bg-gray-100 text-gray-600'
                }`}
              >
                {prenom !== '' ? `${prenom} est ` : ''}
                {PLACE[prochaine.maPlace]?.texte ?? prochaine.maPlace}
              </span>
              {prochaine.monRole && <span className="text-gray-600">{prochaine.monRole}</span>}
            </p>
          )}

          {prochaine.commentaire && (
            <p className="mt-2 text-sm text-gray-600">{prochaine.commentaire}</p>
          )}
        </div>
      )}

      {suivantes.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100">
          {suivantes.map((r: any) => (
            <li key={r.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2 text-sm">
              <span className="text-gray-800">
                {r.aDomicile ? 'Contre' : 'Chez'} {r.adversaire}
                {r.competition ? ` · ${r.competition}` : ''}
              </span>
              <span className="text-gray-500 first-letter:uppercase">{jourEtHeure(r.debut)}</span>
            </li>
          ))}
        </ul>
      )}

      {passees.length > 0 && (
        <details className="mt-3 border-t border-gray-100 pt-3">
          <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-900">
            Rencontres passées ({passees.length})
            {bilan !== null && (
              <span className="text-gray-500">
                {' '}
                — {bilan.presences} présence{bilan.presences > 1 ? 's' : ''} sur {bilan.jouees}{' '}
                match{bilan.jouees > 1 ? 's' : ''}
              </span>
            )}
          </summary>

          <ul className="mt-3 divide-y divide-gray-100">
            {passees.map((r: any) => (
              <li key={r.id} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2 text-sm">
                <span className="text-gray-700">
                  {r.aDomicile ? 'Contre' : 'Chez'} {r.adversaire}
                  {r.maPlace && (
                    <span className="ml-2 text-xs text-gray-500">
                      {PLACE[r.maPlace]?.texte ?? r.maPlace}
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-2">
                  {r.statut === 'annulee' ? (
                    <span className="text-xs text-gray-500">annulée</span>
                  ) : r.statut === 'reportee' ? (
                    <span className="text-xs text-gray-500">reportée</span>
                  ) : (
                    r.score && (
                      <>
                        <span className="tabular-nums text-gray-800">{r.score}</span>
                        {r.issue && (
                          <span className={`text-xs ${ISSUE[r.issue]?.classe ?? 'text-gray-500'}`}>
                            {ISSUE[r.issue]?.texte ?? r.issue}
                          </span>
                        )}
                      </>
                    )
                  )}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
